import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import {
  Calendar,
  CircleCheck,
  Clock,
  House,
  RotateCw,
  SquarePen,
  Users,
} from "lucide-react";

import messages from "../../../assets/locale/messages";
import { ROUTE_PATHS } from "../../../utils/RoutesPaths";
import { adminPermissionModules, eventStatuses } from "../../../utils/Constants";
import { useAdminModulePermission } from "../../../utils/adminPortalHelpers";
import {
  getAdminDashboardApplicantEngagementRequest,
  getAdminDashboardCompanySatisfactionRequest,
  getAdminDashboardOverviewRequest,
  getAdminDashboardQuickActionsRequest,
  getAdminDashboardRecentActivityRequest,
  getAdminDashboardTopCompaniesRequest,
  getAdminDashboardTopEventsRequest,
} from "../../../store/AdminDashboard/actions";
import ApplicantEngagement from "../../../components/Dashboard/ApplicantEngagement";
import CompaniesByStatus from "../../../components/Dashboard/CompaniesByStatus";
import DashboardHeader from "../../../components/Dashboard/DashboardHeader";
import DashboardRefresh from "../../../components/Dashboard/DashboardRefresh";
import QuickActions from "../../../components/Dashboard/QuickActions";
import RecentActivityLog from "../../../components/Dashboard/RecentActivityLog";
import { toActivityLogRow } from "../../../components/Dashboard/RecentActivityLog/constants";
import SatisfactionSection from "../../../components/Dashboard/SatisfactionSection";
import StatCard from "../../../components/Dashboard/StatCard";
import TopCompaniesBySaudiApplicants from "../../../components/Dashboard/TopCompaniesBySaudiApplicants";
import TopEventsTable from "../../../components/Dashboard/TopEventsTable";
import TotalEventsChart from "../../../components/Dashboard/TotalEventsChart";
import "./Dashboard.scss";

/**
 * Live via GET /dashboard/overview (PROLINE-3755): `events_by_status` (KPI
 * Total Events + the donut), `companies_by_status` (KPI Total Companies +
 * Companies by Status), `clients_count`, `applicants_count`, `working_hours`.
 * The API returns no change/delta for any of these — a KPI's `delta` line
 * shows its own "no data yet" message when the value is 0, and otherwise
 * nothing (only Total Working Hours, a last-30-days figure, keeps the plain
 * period label — no arrow).
 *
 * Confirmed by Ahmed Hassan (PROLINE-3715 Jira thread): the banner's Total
 * Events KPI and the donut's total legitimately differ — the KPI counts only
 * upcoming + ongoing + completed (a pending draft nobody published and a
 * cancelled event aren't work the platform delivered), while the donut shows
 * every status including pending/cancelled.
 */
const BANNER_STAT_KEYS = [
  "totalCompanies",
  "totalEvents",
  "totalClients",
  "totalApplicants",
  "totalWorkingHours",
];

/**
 * Maps the donut / KPI's `id` (design labels) to the API's `events_by_status
 * .counts` key. `past` → "Completed", `canceled` → "Cancelled".
 */
const EVENT_STATUS_API_KEY = {
  completed: eventStatuses.past,
  upcoming: eventStatuses.upcoming,
  ongoing: eventStatuses.ongoing,
  draft: eventStatuses.draft,
  pending: eventStatuses.pendingPayment,
  cancelled: eventStatuses.cancelled,
};

/**
 * Icon + route + `quick_actions` field per request type (PROLINE-3718/3755).
 * A row is left out when its count comes back `null` (not permitted). No
 * "Delete Requests" entry — struck through in the story's own acceptance
 * criteria, and the API has no field for it either.
 */
const QUICK_ACTION_META = [
  {
    id: "registration",
    field: "registration_requests_count",
    Icon: Users,
    route: ROUTE_PATHS.registrationRequests,
  },
  {
    id: "profileEdit",
    field: "profile_edit_requests_count",
    Icon: SquarePen,
    route: ROUTE_PATHS.profileEditRequests,
  },
  {
    id: "demo",
    field: "demo_requests_count",
    Icon: RotateCw,
    route: ROUTE_PATHS.demoRequests,
  },
];

/** The API calls a completed event `past`; the table's status labels/colours call it `completed`. */
const TOP_EVENT_STATUS = { past: "completed" };

const formatCount = (value) => Number(value).toLocaleString("en-US");

const formatMonthYear = (date, lang) =>
  new Date(date).toLocaleDateString(lang === "ar" ? "ar-EG-u-nu-latn" : "en-US", {
    month: "short",
    year: "numeric",
  });

const AdminDashboardPage = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  const lang = useSelector((state) => state.locale.lang);
  const login = useSelector((state) => state.auth.login);
  const overview = useSelector((state) => state.adminDashboard.overview);
  const overviewLoading = useSelector(
    (state) => state.adminDashboard.overviewLoading,
  );
  const quickActions = useSelector(
    (state) => state.adminDashboard.quickActions,
  );
  const quickActionsLoading = useSelector(
    (state) => state.adminDashboard.quickActionsLoading,
  );
  const topCompanies = useSelector((state) => state.adminDashboard.topCompanies);
  const topCompaniesLoading = useSelector(
    (state) => state.adminDashboard.topCompaniesLoading,
  );
  const topEvents = useSelector((state) => state.adminDashboard.topEvents);
  const topEventsLoading = useSelector(
    (state) => state.adminDashboard.topEventsLoading,
  );
  const companySatisfaction = useSelector(
    (state) => state.adminDashboard.companySatisfaction,
  );
  const companySatisfactionLoading = useSelector(
    (state) => state.adminDashboard.companySatisfactionLoading,
  );
  const applicantEngagement = useSelector(
    (state) => state.adminDashboard.applicantEngagement,
  );
  const applicantEngagementLoading = useSelector(
    (state) => state.adminDashboard.applicantEngagementLoading,
  );
  const recentActivity = useSelector(
    (state) => state.adminDashboard.recentActivity,
  );
  const recentActivityLoading = useSelector(
    (state) => state.adminDashboard.recentActivityLoading,
  );
  const { dashboard, shared } = messages[lang];
  // Story: the "Create Event" action requires the Events add permission.
  const { canAdd: canAddEvent } = useAdminModulePermission(
    adminPermissionModules.events,
  );

  const [updatedAt, setUpdatedAt] = useState(() => Date.now());

  /** Re-fetches every section's data and re-stamps "updated". */
  const fetchDashboard = useCallback(() => {
    dispatch(getAdminDashboardOverviewRequest());
    dispatch(getAdminDashboardQuickActionsRequest());
    dispatch(getAdminDashboardTopEventsRequest());
    dispatch(getAdminDashboardTopCompaniesRequest());
    dispatch(getAdminDashboardCompanySatisfactionRequest());
    dispatch(getAdminDashboardApplicantEngagementRequest());
    dispatch(getAdminDashboardRecentActivityRequest());
    setUpdatedAt(Date.now());
  }, [dispatch]);

  // `lang` re-fetches on a language switch: company names follow the request locale.
  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard, lang]);

  // KPI strip — live via `overview`, no per-KPI change data from the API (see
  // BANNER_STAT_KEYS comment above): 0 falls back to the KPI's own "no data
  // yet" message; a real value shows no arrow, and only Total Working Hours
  // shows the period label.
  const stats = useMemo(() => {
    if (!overview) return [];

    const bannerEventCounts = overview.events_by_status?.counts || {};

    const bannerValues = {
      totalCompanies: overview.companies_by_status?.total ?? 0,
      // Upcoming + ongoing + completed only — excludes pending and cancelled.
      totalEvents:
        (bannerEventCounts.upcoming ?? 0) +
        (bannerEventCounts.ongoing ?? 0) +
        (bannerEventCounts.past ?? 0),
      totalClients: overview.clients_count ?? 0,
      totalApplicants: overview.applicants_count ?? 0,
      totalWorkingHours: overview.working_hours ?? 0,
    };

    return BANNER_STAT_KEYS.map((key) => {
      const value = bannerValues[key];
      const base = {
        id: key,
        label: dashboard.stats.admin[key],
        info: dashboard.stats.admin.info[key],
        value:
          key === "totalWorkingHours"
            ? dashboard.stats.admin.hoursUnit.replace(
                "{value}",
                formatCount(value),
              )
            : formatCount(value),
      };

      if (!value) {
        return {
          ...base,
          delta: dashboard.stats.admin.empty[key],
          deltaTone: "muted",
        };
      }

      // Only Total Working Hours is a last-30-days figure, so only it keeps the
      // period label; the other totals are platform-wide (no supporting line).
      if (key !== "totalWorkingHours") return base;

      return {
        ...base,
        delta: dashboard.stats.period,
        deltaTone: "neutral",
      };
    });
  }, [dashboard, overview]);

  // Same wording as the Events list page's status tabs (shared.eventStatusOptions);
  // the table's completed rows use `completed` (see TOP_EVENT_STATUS).
  const topEventStatusLabels = useMemo(
    () => ({
      ...shared.eventStatusOptions,
      completed: shared.eventStatusOptions.past,
    }),
    [shared],
  );

  // events_overview (PROLINE-3826) arrives already ranked and capped at 3.
  const topEventRows = useMemo(
    () =>
      topEvents.map((event, index) => ({
        id: event.id,
        rank: index + 1,
        name: event.name,
        imageUrl: event.image_url,
        company: {
          id: event.company?.id,
          name: event.company?.name,
          imageUrl: event.company?.image_url,
          activeSince: dashboard.topEventsByApplicants.activeSince.replace(
            "{value}",
            formatMonthYear(event.company?.approved_at, lang),
          ),
        },
        applicants: event.number_of_approved_requests,
        status: TOP_EVENT_STATUS[event.status] ?? event.status,
      })),
    [dashboard, lang, topEvents],
  );

  const eventsBreakdown = useMemo(() => {
    const counts = overview?.events_by_status?.counts || {};
    return Object.entries(EVENT_STATUS_API_KEY).map(([id, apiKey]) => ({
      id,
      value: counts[apiKey] ?? 0,
      // Same wording as the Events list page's own status tabs (shared.eventStatusOptions
      // is keyed like the API: apiKey, not the design-side id).
      label: shared.eventStatusOptions[apiKey],
    }));
  }, [shared, overview]);

  const companyStatuses = useMemo(() => {
    const counts = overview?.companies_by_status?.counts || {};
    return ["approved", "pending", "deactivated"].map((id) => ({
      id,
      count: counts[id] ?? 0,
      label: dashboard.companiesByStatus.status[id],
    }));
  }, [dashboard, overview]);

  const topCompaniesBySaudiApplicants = useMemo(() => {
    const copy = dashboard.topCompaniesBySaudiApplicants;

    // companies_overview (PROLINE-3826) arrives already ranked and capped at 3.
    // It has no "change this month" or "spread across N events" figure, so those
    // two lines stay off the row until the API provides them.
    return topCompanies.map((company, index) => ({
      id: company.id,
      rank: index + 1,
      name: company.name,
      imageUrl: company.image_url,
      activeSince: copy.activeSince.replace(
        "{value}",
        formatMonthYear(company.approved_at, lang),
      ),
      eventsTotal: company.number_of_published_events,
      saudiApplicants: company.number_of_saudi_applicants,
    }));
  }, [dashboard, lang, topCompanies]);

  // companies_overview?has_evaluated_events=true (PROLINE-3826), lowest score first.
  const companySatisfactionItems = useMemo(() => {
    const copy = dashboard.companySatisfaction;

    return companySatisfaction.map((company) => {
      const clients = company.number_of_evaluating_clients;
      const events = company.number_of_evaluated_events;

      return {
        id: company.id,
        name: company.name,
        imageUrl: company.image_url,
        score: company.overall_clients_satisfaction,
        subtitle: [
          (clients === 1 ? copy.clientsOne : copy.clients).replace(
            "{count}",
            formatCount(clients),
          ),
          (events === 1 ? copy.eventsOne : copy.events).replace(
            "{count}",
            formatCount(events),
          ),
        ].join(" · "),
      };
    });
  }, [dashboard, companySatisfaction]);

  // companies_overview (PROLINE-3853): companies with no value (`null` — no
  // ongoing or past event with applicants) are left out, not drawn as 0.
  const applicantEngagementData = useMemo(
    () =>
      applicantEngagement
        .filter((company) => company.junior_applicants_percentage != null)
        .map((company) => ({
          id: company.id,
          label: company.name,
          value: company.junior_applicants_percentage,
        })),
    [applicantEngagement],
  );

  const quickActionItems = useMemo(
    () =>
      QUICK_ACTION_META.filter(
        (meta) => quickActions?.[meta.field] != null,
      ).map((meta) => {
        const copy = dashboard.quickActions.types[meta.id];

        return {
          id: meta.id,
          icon: meta.Icon,
          label: copy.label,
          description: copy.description,
          count: quickActions[meta.field],
          onClick: () => history.push(meta.route),
        };
      }),
    [dashboard, history, quickActions],
  );

  const recentActivityItems = useMemo(
    () => recentActivity.map((entry) => toActivityLogRow(entry)),
    [recentActivity],
  );

  const eventsTotal = useMemo(
    () => overview?.events_by_status?.total ?? 0,
    [overview],
  );

  // Live via overview (PROLINE-3841): the attendance / late / no-show figures
  // cover the events running right now (not the last 30 days), and the Yoram
  // card counts app users. The API returns no month-over-month change for any
  // of these, so a value shows the card's own supporting line instead of an arrow.
  const platformStats = useMemo(() => {
    const copy = dashboard.platformStats;
    const activeCompaniesValue = overview?.companies_by_status?.counts?.approved ?? 0;
    const activeEventsValue = overview?.events_by_status?.counts?.ongoing ?? 0;

    const yoramCount = overview?.yoram_applicants_count ?? 0;
    const yoramJoined = overview?.yoram_applicants_in_ongoing_events_count ?? 0;
    const yoramAvailable = overview?.yoram_applicants_available_count ?? 0;
    const yoramSaudiPercent = overview?.yoram_applicants_saudi_percentage;

    // `null` = no ongoing event has both an applicant and a check-in day yet.
    const attendanceRate = overview?.ongoing_events_overall_attendance_rate;
    const lateAndNoShowValue =
      (overview?.ongoing_events_late_check_ins_count ?? 0) +
      (overview?.ongoing_events_no_shows_count ?? 0);

    return [
      {
        id: "activeCompanies",
        icon: House,
        tone: "violet",
        value: formatCount(activeCompaniesValue),
        label: copy.activeCompanies.label,
        change: null,
        emptyMessage: activeCompaniesValue
          ? dashboard.stats.period
          : copy.activeCompanies.empty,
      },
      {
        id: "activeEvents",
        icon: Calendar,
        tone: "violet",
        value: formatCount(activeEventsValue),
        label: copy.activeEvents.label,
        change: null,
        emptyMessage: activeEventsValue
          ? dashboard.stats.period
          : copy.activeEvents.empty,
      },
      {
        id: "workingApplicants",
        icon: Users,
        tone: "green",
        value: formatCount(yoramCount),
        label: copy.workingApplicants.label,
        emptyMessage: copy.workingApplicants.empty,
        // Story: hide the breakdown and the Saudi% tag entirely when there
        // are no applicants yet.
        breakdown: yoramCount
          ? [
              {
                id: "joinedEvents",
                value: formatCount(yoramJoined),
                label: copy.workingApplicants.joinedEvents,
              },
              {
                id: "availableToJoin",
                value: formatCount(yoramAvailable),
                label: copy.workingApplicants.availableToJoin,
              },
            ]
          : [],
        // Story: displayed as a whole number, unlike the other % KPIs.
        tag:
          yoramCount && yoramSaudiPercent != null
            ? {
                value: Math.round(yoramSaudiPercent),
                label: copy.workingApplicants.saudiTag,
                color: "success",
              }
            : null,
      },
      {
        id: "attendanceRate",
        icon: CircleCheck,
        tone: "amber",
        // Shown exactly as the API returns it — no forced decimal, no rounding.
        value: String(Number(attendanceRate ?? 0)),
        valueSuffix: "%",
        label: copy.attendanceRate.label,
        info: copy.attendanceRate.info,
        change: null,
        emptyMessage:
          attendanceRate == null
            ? copy.attendanceRate.empty
            : dashboard.stats.period,
      },
      {
        id: "lateAndNoShow",
        icon: Clock,
        tone: "red",
        value: formatCount(lateAndNoShowValue),
        label: copy.lateAndNoShow.label,
        info: copy.lateAndNoShow.info,
        change: null,
        emptyMessage: lateAndNoShowValue
          ? dashboard.stats.period
          : copy.lateAndNoShow.empty,
        // Story: no late check-ins or no-shows is a good outcome.
        emptyTone: lateAndNoShowValue ? "muted" : "positive",
      },
    ];
  }, [dashboard, overview]);

  return (
    <div className="admin-dashboard">
      <DashboardRefresh
        labels={{
          refresh: dashboard.refresh.action,
          refreshing: dashboard.refresh.refreshing,
          justNow: dashboard.refresh.justNow,
          minutesAgo: dashboard.refresh.minutesAgo,
          hoursAgo: dashboard.refresh.hoursAgo,
        }}
        updatedAt={updatedAt}
        loading={
          overviewLoading ||
          quickActionsLoading ||
          topEventsLoading ||
          topCompaniesLoading ||
          companySatisfactionLoading ||
          applicantEngagementLoading ||
          recentActivityLoading
        }
        onRefresh={fetchDashboard}
      />

      <DashboardHeader
        breadcrumbLabel={dashboard.breadcrumb}
        userName={login?.role?.name || login?.name || ""}
        userImage={login?.image_url}
        welcomeTemplate={dashboard.header.welcomeBack}
        welcomeFallback={dashboard.header.welcomeBackNoName}
        subTitle={dashboard.header.platformOverview}
        primaryAction={
          canAddEvent
            ? {
                label: dashboard.header.createEvent,
                onClick: () => history.push(ROUTE_PATHS.createEvent),
              }
            : null
        }
        stats={stats}
      />

      <div className="admin-dashboard__stats">
        {platformStats.map(({ id, ...card }) => (
          <StatCard key={id} {...card} />
        ))}
      </div>


      <div className="admin-dashboard__grid">
        <TopEventsTable
          title={dashboard.topEventsByApplicants.title}
          viewEventsLabel={dashboard.topEventsByApplicants.viewEvents}
          columns={dashboard.topEventsByApplicants.columns}
          statusLabels={topEventStatusLabels}
          rows={topEventRows}
          emptyTitle={dashboard.topEventsByApplicants.empty.title}
          emptyDescription={dashboard.topEventsByApplicants.empty.description}
          emptyActionLabel={dashboard.header.createEvent}
          onViewEvents={() => history.push(ROUTE_PATHS.events)}
          onEmptyAction={() => history.push(ROUTE_PATHS.createEvent)}
          onRowClick={(row) =>
            history.push(ROUTE_PATHS.viewEvent.replace(":id", row.id))
          }
          onCompanyClick={(company) =>
            history.push(
              ROUTE_PATHS.companiesListCompanyDetails.replace(":id", company.id),
            )
          }
        />

        <TotalEventsChart
          title={dashboard.platformEvents.title}
          totalLabel={dashboard.totalEvents.totalLabel}
          total={eventsTotal}
          breakdown={eventsBreakdown}
          emptyTitle={dashboard.platformEvents.empty.title}
          emptyDescription={dashboard.platformEvents.empty.description}
          classNames={{
            body: "admin"
          }}
        />
      </div>

      <div className="admin-dashboard__companies">
        <CompaniesByStatus
          title={dashboard.companiesByStatus.title}
          totalLabel={dashboard.companiesByStatus.totalLabel}
          total={overview?.companies_by_status?.total ?? 0}
          statuses={companyStatuses}
          emptyTitle={dashboard.companiesByStatus.empty.title}
          emptyDescription={dashboard.companiesByStatus.empty.description}
          emptyActionLabel={dashboard.companiesByStatus.empty.action}
          onEmptyAction={() =>
            history.push(ROUTE_PATHS.registrationRequests)
          }
        />

        <TopCompaniesBySaudiApplicants
          title={dashboard.topCompaniesBySaudiApplicants.title}
          viewCompaniesLabel={dashboard.topCompaniesBySaudiApplicants.viewCompanies}
          columns={dashboard.topCompaniesBySaudiApplicants.columns}
          rows={topCompaniesBySaudiApplicants}
          emptyTitle={dashboard.topCompaniesBySaudiApplicants.empty.title}
          emptyDescription={
            dashboard.topCompaniesBySaudiApplicants.empty.description
          }
          onViewCompanies={() => history.push(ROUTE_PATHS.companiesList)}
          onRowClick={(row) =>
            history.push(
              ROUTE_PATHS.companiesListCompanyDetails.replace(":id", row.id),
            )
          }
        />
      </div>

      <SatisfactionSection
        title={dashboard.companySatisfaction.title}
        overallLabel={dashboard.satisfaction.overall}
        bandLabels={dashboard.companySatisfaction.bands}
        className="satisfaction--company"
        items={companySatisfactionItems}
        viewAllLabel={dashboard.companySatisfaction.viewAllCompanies}
        onViewAll={() => history.push(ROUTE_PATHS.companiesList)}
        showMoreLabel={dashboard.showMore}
        showLessLabel={dashboard.showLess}
        emptyTitle={dashboard.companySatisfaction.empty.title}
        emptyDescription={dashboard.companySatisfaction.empty.description}
      />

      <ApplicantEngagement
        title={dashboard.applicantEngagement.title}
        description={dashboard.applicantEngagement.description}
        data={applicantEngagementData}
        viewAllLabel={dashboard.applicantEngagement.viewAllCompanies}
        onViewAll={() => history.push(ROUTE_PATHS.companiesList)}
        emptyTitle={dashboard.applicantEngagement.empty.title}
        emptyDescription={dashboard.applicantEngagement.empty.description}
      />

      <div className="admin-dashboard__activity">
        <QuickActions
          title={dashboard.quickActions.title}
          items={quickActionItems}
          emptyTitle={dashboard.quickActions.empty.title}
          emptyDescription={dashboard.quickActions.empty.description}
        />

        <RecentActivityLog
          title={dashboard.recentActivity.title}
          items={recentActivityItems}
          modules={dashboard.recentActivity.modules}
          lang={lang}
          limit={10}
          showCompany
          fullLogsLabel={dashboard.recentActivity.fullLogs}
          onFullLogs={() => history.push(ROUTE_PATHS.adminActivityLog)}
          emptyTitle={dashboard.recentActivity.empty.title}
          emptyDescription={dashboard.recentActivity.empty.description}
        />
      </div>
    </div>
  );
};

export default AdminDashboardPage;
